/**
 * RootErrorElement — React error boundary for all route errors.
 *
 * DEV: shows full error details (message, status, stack trace) for debugging.
 * PROD: renders branded ErrorPage — no technical details exposed.
 *
 * Catches:
 *   - 404 Response → NotFoundPage
 *   - 500/503 Response → ErrorPage (dev: DevErrorView first)
 *   - Generic JS Error (ReferenceError, TypeError, etc.) → ErrorPage / DevErrorView
 *
 * Used as `errorElement` on the catch-all `*` route.
 *
 * @layer-1 — shared component
 */
import { isRouteErrorResponse, useRouteError } from "react-router";
// Static imports: RootErrorBoundary already imports these synchronously, so
// Rollup cannot split them out (INEFFECTIVE_DYNAMIC_IMPORT). Loading a chunk
// while an error is already on screen is also fragile — keep error UI eager.
import { NotFoundPage } from "@/features/errors/components/NotFoundPage";
import { ErrorPage } from "@/features/errors/components/ErrorPage";
import { Logo } from "@/shared/components/Logo";
import { cn } from "@/lib/utils";
import { authPalette } from "@/shared/lib/design-tokens/colors";

/**
 * Dev-only detailed error view — never shown in production.
 * Shows error type, status, message, and stack trace for fast debugging.
 */
function DevErrorView({ error }: { error: unknown }) {
  const isRR = isRouteErrorResponse(error);
  const status = isRR ? error.status : null;
  const statusText = isRR ? error.statusText : null;
  const data = isRR ? error.data : null;
  const message = isRR
    ? statusText ?? (status === 404 ? "Not Found" : "Server Error")
    : error instanceof Error
    ? error.message
    : String(error);
  const stack = error instanceof Error ? error.stack : null;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8"
      style={{ background: authPalette.warmPaper, fontFamily: "var(--font-sans)" }}
    >
      <div className="w-full max-w-[600px]">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo height={48} />
          </div>
        </div>

        {/* Error card */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: authPalette.white,
            border: "1px solid",
            borderColor: authPalette.borderLight,
            boxShadow: authPalette.cardShadow,
          }}
        >
          {/* Status badge */}
          <div className="px-6 pt-6 pb-2 flex items-start gap-3">
            <span
              className={cn(
                "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest",
                status === 404
                  ? "text-yellow-700 bg-yellow-50"
                  : "text-red-700 bg-red-50"
              )}
            >
              {status ?? "Error"} {statusText}
            </span>
            <span className="text-xs text-gray-400 font-mono mt-0.5">
              {isRR ? "RouteErrorResponse" : "Generic Error"}
            </span>
          </div>

          {/* Message */}
          <div className="px-6 pb-4">
            <p
              className="text-xl font-bold mb-1"
              style={{ fontFamily: "var(--font-display)", color: authPalette.ink }}
            >
              {message}
            </p>
            {isRR && data ? (
              <details className="mt-2">
                <summary className="text-xs cursor-pointer text-gray-400 hover:text-gray-600">
                  Response data
                </summary>
                <pre
                  className="mt-2 p-3 rounded-lg text-xs overflow-auto max-h-48"
                  style={{ background: authPalette.surfaceCard, color: authPalette.mutedDim }}
                >
                  {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
                </pre>
              </details>
            ) : null}
          </div>

          {/* Stack trace */}
          {stack ? (
            <div className="mx-6 mb-6">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-bold">
                Stack Trace
              </p>
              <pre
                className="p-3 rounded-lg text-xs overflow-auto max-h-48"
                style={{
                  background: authPalette.ink,
                  color: authPalette.borderLight,
                  fontFamily: "monospace",
                }}
              >
                {stack}
              </pre>
            </div>
          ) : null}

          {/* Action */}
          <div className="px-6 pb-6">
            <button
              onClick={() => window.location.reload()}
              className="w-full h-10 rounded-xl font-bold text-[11px] uppercase tracking-[0.16em] transition-all cursor-pointer"
              style={{
                background: authPalette.ink,
                color: authPalette.white,
                fontFamily: "var(--font-sans)",
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Must be called inside a Router context (useRouteError) */
function RootErrorContent() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) return <NotFoundPage />;
    // 5xx — dev: details; prod: branded page
    if (import.meta.env.DEV) return <DevErrorView error={error} />;
    return <ErrorPage />;
  }

  // Generic JS error (TypeError, ReferenceError, etc.) — dev: details; prod: branded page
  if (import.meta.env.DEV) return <DevErrorView error={error} />;
  return <ErrorPage />;
}

/** Export as a component — React Router calls this as a render function */
export function RootErrorElement() {
  return <RootErrorContent />;
}
